// Centralized API client for ClaraNarratio v2 PWA

// Set base URL dynamically
const getBaseUrl = (): string => {
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return '';
    }
  }
  return 'https://corpora.wikiboox.com';
};

const BASE_URL = getBaseUrl();

export interface BookManifestItem {
  id: string;
  title: string;
  author: string;
  coverUrl?: string;
  duration?: number;
  isBookClub?: boolean;
  hasAudio?: boolean;
  trackCount?: number;
  playlistUrl?: string;
  audioUrl?: string;
  audioStatus?: string;
}

export interface EntitlementResponse {
  isPro: boolean;
  isBookClubMember: boolean;
}

export interface PlaylistTrack {
  key?: string;
  url?: string;
  index: number;
  size?: number;
}

export interface PlaylistResponse {
  ok?: boolean;
  tracks: PlaylistTrack[];
  error?: string;
}

class ApiClientService {
  async fetchManifest(): Promise<BookManifestItem[]> {
    // Try v2 first
    try {
      const res = await fetch(`${BASE_URL}/api/library/manifest?v=2`);
      if (res.ok) {
        const d = await res.json();
        if (d && Array.isArray(d.titles)) {
          return d.titles.map((item: any) => ({
            id: item.id,
            title: item.title,
            author: item.author,
            coverUrl: `/api/library/cover/${item.id}`,
            playlistUrl: `/api/library/playlist/${item.id}`,
            hasAudio: item.audio?.status === 'ready' || item.audio?.status === 'completed',
            audioStatus: item.audio?.status,
            duration: item.audio?.durationSec || 0,
            isBookClub: !!item.flags?.bookClub
          }));
        }
      }
    } catch (e) {
      console.warn("Failed to fetch v2 manifest, falling back to v1:", e);
    }

    // Fall back to v1
    const res = await fetch(`${BASE_URL}/api/library/manifest`);
    if (!res.ok) {
      throw new Error(`Failed to fetch manifest: ${res.status}`);
    }
    const d = await res.json();
    if (Array.isArray(d)) {
      return d.map((item: any) => ({
        id: item.id,
        title: item.title,
        author: item.author,
        coverUrl: `/api/library/cover/${item.id}`,
        playlistUrl: item.playlistUrl || `/api/library/playlist/${item.id}`,
        hasAudio: typeof item.isIncluded === 'boolean' ? item.isIncluded : true,
        audioStatus: item.isIncluded ? 'ready' : 'missing',
        duration: item.duration || 0,
        isBookClub: !!item.isBookClub
      }));
    }
    if (d && d.ok && Array.isArray(d.items)) {
      return d.items;
    }
    return [];
  }

  async fetchEntitlement(): Promise<EntitlementResponse> {
    try {
      const res = await fetch(`${BASE_URL}/api/entitlement`);
      if (!res.ok) {
        throw new Error(`Failed to fetch entitlement: ${res.status}`);
      }
      const d = await res.json();
      return {
        isPro: !!d.isPro,
        isBookClubMember: typeof d.isBookClubMember === 'boolean' ? d.isBookClubMember : !!d.isPro
      };
    } catch (e) {
      console.warn("Soft failure fetching entitlement, assuming offline or non-member:", e);
      return { isPro: false, isBookClubMember: false };
    }
  }

  async fetchPlaylist(bookId: string, customUrl?: string): Promise<PlaylistResponse> {
    const targetUrl = customUrl || `${BASE_URL}/api/library/playlist/${bookId}`;
    const res = await fetch(targetUrl);
    if (!res.ok) {
      throw new Error(`Failed to fetch playlist: ${res.status}`);
    }
    const data = await res.json();
    return data;
  }
}

export const ApiClient = new ApiClientService();

export const FALLBACK_BOOKS: BookManifestItem[] = [
  {
    id: "alice-in-wonderland--lewis-carroll",
    title: "Alice's Adventures in Wonderland",
    author: "Lewis Carroll",
    coverUrl: "/api/library/cover/alice-in-wonderland--lewis-carroll",
    playlistUrl: "/api/library/playlist/alice-in-wonderland--lewis-carroll",
    hasAudio: true,
    isBookClub: false
  },
  {
    id: "adventures-of-sherlock-holmes--arthur-conan-doyle",
    title: "The Adventures of Sherlock Holmes",
    author: "Arthur Conan Doyle",
    coverUrl: "/api/library/cover/adventures-of-sherlock-holmes--arthur-conan-doyle",
    playlistUrl: "/api/library/playlist/adventures-of-sherlock-holmes--arthur-conan-doyle",
    hasAudio: true,
    isBookClub: false
  },
  {
    id: "secret-garden--frances-hodgson-burnett",
    title: "The Secret Garden",
    author: "Frances Hodgson Burnett",
    coverUrl: "/api/library/cover/secret-garden--frances-hodgson-burnett",
    playlistUrl: "/api/library/playlist/secret-garden--frances-hodgson-burnett",
    hasAudio: true,
    isBookClub: false
  },
  {
    id: "peter-pan--j-m-barrie",
    title: "Peter Pan",
    author: "J. M. Barrie",
    coverUrl: "/api/library/cover/peter-pan--j-m-barrie",
    playlistUrl: "/api/library/playlist/peter-pan--j-m-barrie",
    hasAudio: true,
    isBookClub: false
  }
];
